'use client';
import React, { forwardRef } from "react";
import { cn } from "../../lib/utilsComprehensive";
import { OptimizedGlass } from "../../primitives";
import { Motion } from "../../primitives";
import {
  useA11yId,
  createModalA11y,
  keyboardHandlers,
  announceToScreenReader,
} from "../../utils/a11y";
import { useMotionPreferenceContext } from "../../contexts/MotionPreferenceContext";
import { LiquidGlassAdaptiveSheet } from "./LiquidGlassAdaptiveSheet";

export interface GlassBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  height?: number | string; // e.g. 70% or 560
  className?: string;
  /**
   * Accessible title for the bottom sheet
   */
  title?: string;
  /**
   * Accessible description for the bottom sheet
   */
  description?: string;
  /**
   * Whether to respect motion preferences
   */
  respectMotionPreference?: boolean;
  /**
   * Custom aria-label
   */
  "aria-label"?: string;
  /**
   * Custom aria-labelledby
   */
  "aria-labelledby"?: string;
  /**
   * Custom aria-describedby
   */
  "aria-describedby"?: string;
  /**
   * Testing attribute
   */
  "data-testid"?: string;
  /**
   * Material system to use for the sheet surface.
   */
  material?: "glass" | "liquid";
  /**
   * Liquid material variant.
   */
  materialVariant?: "regular" | "clear";
  /**
   * Presentation mode for liquid sheets.
   */
  presentationMode?: "interruptive" | "parallel";
  /**
   * Source id for Liquid Glass source transitions.
   */
  sourceId?: string;
}

export const GlassBottomSheet = forwardRef<
  HTMLDivElement,
  GlassBottomSheetProps
>(
  (
    {
      open,
      onOpenChange,
      children,
      height = "70%",
      className,
      title,
      description,
      respectMotionPreference = true,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-describedby": ariaDescribedBy,
      material = "glass",
      materialVariant = "regular",
      presentationMode = "interruptive",
      sourceId,
      ...props
    },
    ref
  ) => {
    // Generate unique IDs for accessibility
    const bottomSheetId = useA11yId("glass-bottom-sheet");
    const titleId = title ? useA11yId("glass-bottom-sheet-title") : undefined;
    const descriptionId = description
      ? useA11yId("glass-bottom-sheet-desc")
      : undefined;
    const { prefersReducedMotion } = useMotionPreferenceContext();

    // Create accessibility attributes
    const a11yProps = createModalA11y({
      id: bottomSheetId,
      titleId: ariaLabelledBy || titleId,
      descriptionId: ariaDescribedBy || descriptionId,
      modal: true,
    });

    // Add aria-label if provided (or use default if no title/label)
    if (ariaLabel) {
      a11yProps["aria-label"] = ariaLabel;
    } else if (!ariaLabelledBy && !titleId) {
      // Default aria-label if none provided
      a11yProps["aria-label"] = title || "Bottom sheet";
    }

    // Handle escape key
    const handleEscape = keyboardHandlers.onEscape(() => onOpenChange(false));

    // Announce to screen readers when opened
    React.useEffect(() => {
      if (open) {
        announceToScreenReader(
          `Bottom sheet opened: ${title || ariaLabel || "Bottom sheet"}`,
          "polite"
        );
      }
    }, [open, title, ariaLabel]);

    const shouldAnimate = respectMotionPreference
      ? !prefersReducedMotion
      : true;

    if (material === "liquid") {
      return (
        <LiquidGlassAdaptiveSheet
          ref={ref}
          open={open}
          onOpenChange={onOpenChange}
          title={title || ariaLabel}
          height={height}
          side="bottom"
          sourceId={sourceId}
          presentationMode={presentationMode}
          materialVariant={materialVariant}
          className={className}
          aria-describedby={ariaDescribedBy || descriptionId}
          data-testid={props["data-testid"]}
        >
          {description && (
            <p id={descriptionId} className="glass-text-sm glass-text-secondary glass-mb-4">
              {description}
            </p>
          )}
          {children}
        </LiquidGlassAdaptiveSheet>
      );
    }

    return (
      <>
        {open && (
          <Motion
            preset="fadeIn"
            duration={shouldAnimate ? 200 : 0}
            className='glass-fixed glass-inset-0 glass-z-1100'
            onClick={(e) => onOpenChange(false)}
          >
            <div className='glass-absolute glass-inset-0 glass-surface-dark/50' />
          </Motion>
        )}
        <Motion
          preset="slideUp"
          duration={shouldAnimate ? 300 : 0}
          className={cn(
            "fixed left-0 right-0 bottom-0 z-[1101]",
            open ? "pointer-events-auto" : "pointer-events-none",
            className
          )}
          style={{
            height: typeof height === "number" ? `${height}px` : height,
            transform: !shouldAnimate && !open ? "translateY(100%)" : undefined,
          }}
          onKeyDown={handleEscape}
          data-testid={props['data-testid']}
          {...a11yProps}
          {...props}
        >
          <OptimizedGlass
            ref={ref}
            elevation="level3"
            className={cn("h-full rounded-t-2xl glass-p-4 glass-overflow-y-auto glass-on-light")}
            style={{ boxSizing: "border-box" }}
            tabIndex={-1}
          >
            {/* Handle indicator */}
            <div
              className='glass-mx-auto glass-w-10 glass-h-1.5 glass-radius-full glass-surface-subtle/30 glass-mb-3'
              aria-hidden="true"
            />

            {/* Header with title and description */}
            {(title || description) && (
              <div className='glass-mb-4'>
                {title && (
                  <h2
                    id={titleId}
                    className='glass-text-lg glass-font-semibold glass-text-primary'
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    id={descriptionId}
                    className="glass-text-sm glass-text-secondary glass-mt-1"
                  >
                    {description}
                  </p>
                )}
              </div>
            )}

            {children}
          </OptimizedGlass>
        </Motion>
      </>
    );
  }
);

GlassBottomSheet.displayName = "GlassBottomSheet";

export default GlassBottomSheet;
